/**
* TelemetryContext.ts
* @author Hector Hernandez (hectorh)
* @copyright Microsoft 2019
*/
import { isString, objForEachKey } from "@microsoft/1ds-core-js";
import { Application } from "./context/Application";
import { Cloud } from "./context/Cloud";
import { User } from "./context/User";
import { Web } from "./context/Web";
import { OperatingSystem } from "./context/OperatingSystem";
import { IntWeb } from "./context/IntWeb";
import { Utc } from "./context/Utc";
import { Loc } from "./context/Loc";
import { Device } from "./context/Device";
import { Session } from "./context/Session";
import { Trace } from "./context/Trace";
import { Extensions, AppExtensionKeys, UserExtensionKeys, WebExtensionKeys, SDKExtKeys, OSExtKeys, IntWebExtKeys, UtcExtKeys, LocExtKeys, SessionExtKeys, DeviceExtKeys, TraceExtKeys, CloudExtKeys } from "./ExtensionKeys";
import { Sdk } from "./context/Sdk";
import { SessionManager } from "./SessionManager";
import dynamicProto from "@microsoft/dynamicproto-js";
function _applyExtValues(extension, event, names, map) {
    var target = event.ext[Extensions[extension]];
    if (target) {
        objForEachKey(map, function (field, value) {
            if (isString(value)) {
                target[names[field]] = value;
            }
        });
    }
    return target;
}
var TelemetryContext = /** @class */ (function () {
    function TelemetryContext(coreConfig, propertiesConfig, core) {
        dynamicProto(TelemetryContext, this, function (_self) {
            _self.app = new Application(propertiesConfig, core);
            _self.cloud = new Cloud();
            _self.user = new User(coreConfig, propertiesConfig, core);
            _self.os = new OperatingSystem(propertiesConfig);
            _self.web = new Web(propertiesConfig, core);
            var _sdk = new Sdk(coreConfig, core);
            var _intWeb = new IntWeb(propertiesConfig, core);
            var _utc = new Utc(propertiesConfig);
            _self.loc = new Loc();
            _self.device = new Device();
            var _telemetryTrace = new Trace(propertiesConfig);
            var _sessionManager = new SessionManager(core, propertiesConfig);
            _self.session = new Session();
            function _getSessionId() {
                var session = _self.session;
                if (session && isString(session.customId)) {
                    return session.customId;
                }
                _sessionManager.update();
                var autoSession = _sessionManager.automaticSession;
                if (autoSession) {
                    var autoId = autoSession.getId();
                    if (autoId && isString(autoId)) {
                        session.automaticId = autoId;
                    }
                }
                return session.automaticId;
            }
            _self.getSessionId = _getSessionId;
            _self.applyApplicationContext = function (event) {
                var _a;
                var app = _self.app;
                _applyExtValues(4 /* AppExt */, event, AppExtensionKeys, (_a = {},
                    _a[0 /* id */] = app.id,
                    _a[1 /* ver */] = app.ver,
                    _a[2 /* appName */] = app.name,
                    _a[3 /* locale */] = app.locale,
                    _a[4 /* expId */] = app.getExpId(),
                    _a[5 /* env */] = app.env,
                    _a));
            };
            _self.applyUserContext = function (event) {
                var _a;
                var user = _self.user;
                _applyExtValues(0 /* UserExt */, event, UserExtensionKeys, (_a = {},
                    _a[1 /* localId */] = user.getLocalId(),
                    _a[0 /* locale */] = user.locale,
                    _a[2 /* id */] = user.id,
                    _a));
            };
            _self.applyWebContext = function (event) {
                var _a;
                var web = _self.web;
                var evtWeb = _applyExtValues(3 /* WebExt */, event, WebExtensionKeys, (_a = {},
                    _a[0 /* domain */] = web.domain,
                    _a[1 /* browser */] = web.browser,
                    _a[2 /* browserVer */] = web.browserVer,
                    _a[3 /* screenRes */] = web.screenRes,
                    _a[5 /* consentDetails */] = web.getUserConsentDetails(),
                    _a));
                evtWeb[WebExtensionKeys[4 /* userConsent */]] = web.getUserConsent();
            };
            _self.applyOsContext = function (event) {
                var _a;
                var os = _self.os;
                _applyExtValues(5 /* OSExt */, event, OSExtKeys, (_a = {},
                    _a[0 /* osName */] = os.name,
                    _a[1 /* ver */] = os.ver,
                    _a));
            };
            _self.applySdkContext = function (event) {
                var _a;
                var evtSdk = _applyExtValues(6 /* SdkExt */, event, SDKExtKeys, (_a = {},
                    _a[2 /* installId */] = _sdk.installId,
                    _a));
                evtSdk[SDKExtKeys[1 /* seq */]] = _sdk.getSequenceId();
                evtSdk[SDKExtKeys[3 /* epoch */]] = _sdk.epoch;
            };
            _self.applyIntWebContext = function (event) {
                var _a;
                _applyExtValues(7 /* IntWebExt */, event, IntWebExtKeys, (_a = {},
                    _a[0 /* msfpc */] = _intWeb.getMsfpc(),
                    _a[1 /* anid */] = _intWeb.getAnid(),
                    _a[2 /* serviceName */] = _intWeb.serviceName,
                    _a));
            };
            _self.applyUtcContext = function (event) {
                var evtUtc = event.ext[Extensions[8 /* UtcExt */]];
                evtUtc[UtcExtKeys[0 /* popSample */]] = _utc.popSample;
                if (_utc.eventFlags > 0) {
                    evtUtc[UtcExtKeys[1 /* eventFlags */]] = _utc.eventFlags;
                }
            };
            _self.applyLocContext = function (event) {
                event.ext[Extensions[9 /* LocExt */]][LocExtKeys.tz] = _self.loc.tz;
            };
            _self.applySessionContext = function (event) {
                event.ext[Extensions[4 /* AppExt */]][SessionExtKeys[0 /* sessionId */]] = _getSessionId();
            };
            _self.applyDeviceContext = function (event) {
                var _a;
                var device = _self.device;
                _applyExtValues(1 /* DeviceExt */, event, DeviceExtKeys, (_a = {},
                    _a[0 /* localId */] = device.localId,
                    _a[2 /* make */] = device.make,
                    _a[3 /* model */] = device.model,
                    _a[1 /* deviceClass */] = device.deviceClass,
                    _a));
            };
            _self.applyCloudContext = function (event) {
                var _a;
                var cloud = _self.cloud;
                _applyExtValues(10 /* CloudExt */, event, CloudExtKeys, (_a = {},
                    _a[0 /* role */] = cloud.role,
                    _a[1 /* roleInstance */] = cloud.roleInstance,
                    _a[2 /* roleVer */] = cloud.roleVer,
                    _a));
            };
            _self.applyAITraceContext = function (event) {
                var _a;
                _applyExtValues(2 /* TraceExt */, event, TraceExtKeys, (_a = {},
                    _a[0 /* traceId */] = _telemetryTrace.traceId,
                    _a[1 /* traceName */] = _telemetryTrace.name,
                    _a[2 /* parentId */] = _telemetryTrace.parentId,
                    _a));
            };
        });
    }
    /**
     * Returns the current session id, if one does not already exist a new one will be attempted to be created.
     * If you have set a specific custom id via session.setId(), this function will return that value.
     */
    TelemetryContext.prototype.getSessionId = function () {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
        return null;
    };
    TelemetryContext.prototype.applyApplicationContext = function (event) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    TelemetryContext.prototype.applyUserContext = function (event) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    TelemetryContext.prototype.applyWebContext = function (event) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    TelemetryContext.prototype.applyOsContext = function (event) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    TelemetryContext.prototype.applySdkContext = function (event) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    TelemetryContext.prototype.applyIntWebContext = function (event) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    TelemetryContext.prototype.applyUtcContext = function (event) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    TelemetryContext.prototype.applyLocContext = function (event) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    TelemetryContext.prototype.applySessionContext = function (event) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    TelemetryContext.prototype.applyDeviceContext = function (event) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    TelemetryContext.prototype.applyCloudContext = function (event) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    TelemetryContext.prototype.applyAITraceContext = function (event) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    return TelemetryContext;
}());
export { TelemetryContext };
//# sourceMappingURL=TelemetryContext.js.map